/**
 * BANVA (05-oct-2026): «Completa tu compra» dentro del cajón del carro.
 *
 * Cómo funciona:
 *  - El cajón (snippets/header-actions.liquid) trae un contenedor vacío
 *    [data-banva-sug]. El tema re-dibuja ese cajón entero desde el servidor cada
 *    vez que cambia el carro (morph); el contenedor lleva data-skip-node-update y
 *    data-skip-subtree-update para que ese re-dibujo NO borre lo que ponemos acá.
 *  - Cada cambio del carro (evento `cart:update`) pide a Shopify las «relacionadas»
 *    de un producto del carro, ya dibujadas por sections/banva-carro-sugerencias.liquid
 *    (stock real, sin lo que ya está en el carro, precio con el formato de la tienda).
 *  - El «+» agrega por el mismo camino que el botón normal de la ficha y avisa al
 *    cajón con el MISMO evento (CartAddEvent), así el tema actualiza totales y líneas.
 *
 * Observable (Regla 4): el contenedor expone data-estado, data-mostrados y data-base (el producto
 * del carro sobre el que se calcularon las sugerencias):
 *  con-sugerencias | sin-candidatos | sin-recomendaciones | carro-vacio |
 *  error-carro | error-recomendaciones | respuesta-sin-marca.
 * Los errores se loguean con [banva-sug]; nada se traga en silencio.
 */
import { ThemeEvents, CartAddEvent } from '@theme/events';

const SECCION = 'banva-carro-sugerencias';
// Se piden de más: la sección descarta lo agotado y lo que ya está en el carro, y muestra 3.
const PEDIR = 10;
const ESPERA_MS = 150;
const CONTENEDOR = '[data-banva-sug]';

/** @type {number} */
let secuencia = 0;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let temporizador;

/**
 * @param {Iterable<HTMLElement>} contenedores
 * @param {string} estado
 * @param {number} [mostrados]
 * @param {string} [base] id del producto del carro sobre el que se calcularon las sugerencias ('' si no hubo cálculo)
 */
function marcar(contenedores, estado, mostrados = 0, base = '') {
  for (const c of contenedores) {
    c.dataset.estado = estado;
    c.dataset.mostrados = String(mostrados);
    c.dataset.base = base;
  }
}

/** @returns {string} */
function raizDeRutas() {
  return window.Shopify?.routes?.root ?? '/';
}

/**
 * @param {string | undefined} productoPreferido id del producto que el cliente acaba de tocar (si se sabe)
 */
async function refrescar(productoPreferido) {
  const contenedores = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(CONTENEDOR)]);
  if (!contenedores.length) return;
  const yo = ++secuencia;

  /** @type {{ items: { product_id: number }[] }} */
  let carro;
  try {
    const r = await fetch(`${raizDeRutas()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(`cart.js respondió ${r.status}`);
    carro = await r.json();
  } catch (error) {
    console.error('[banva-sug] no se pudo leer el carro', error);
    for (const c of contenedores) c.replaceChildren(); // mejor nada que sugerencias que ya no corresponden
    marcar(contenedores, 'error-carro');
    return;
  }
  if (yo !== secuencia) return; // llegó un cambio más nuevo: esta respuesta ya no vale

  if (!carro.items.length) {
    for (const c of contenedores) c.replaceChildren();
    marcar(contenedores, 'carro-vacio');
    return;
  }

  const base = carro.items.find((i) => String(i.product_id) === String(productoPreferido)) ?? carro.items[0];
  const url = `${raizDeRutas()}recommendations/products?section_id=${SECCION}&product_id=${base.product_id}&limit=${PEDIR}&intent=related`;

  let html;
  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`recomendaciones respondió ${r.status}`);
    html = await r.text();
  } catch (error) {
    console.error('[banva-sug] no se pudieron pedir las sugerencias', error);
    for (const c of contenedores) c.replaceChildren(); // mejor nada que sugerencias que ya no corresponden
    marcar(contenedores, 'error-recomendaciones');
    return;
  }
  if (yo !== secuencia) return;

  const raiz = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-banva-sug-estado]');
  if (!raiz) {
    console.error('[banva-sug] la respuesta no trae la marca esperada (¿se borró la sección?)');
    for (const c of contenedores) c.replaceChildren();
    marcar(contenedores, 'respuesta-sin-marca');
    return;
  }

  const estado = /** @type {HTMLElement} */ (raiz).dataset.banvaSugEstado ?? 'respuesta-sin-marca';
  const mostrados = Number(/** @type {HTMLElement} */ (raiz).dataset.mostrados ?? 0);
  for (const c of contenedores) {
    if (estado === 'con-sugerencias') c.replaceChildren(document.importNode(raiz, true));
    else c.replaceChildren();
  }
  marcar(contenedores, estado, mostrados, String(base.product_id));
}

/** @param {string | undefined} [productoPreferido] */
function programar(productoPreferido) {
  clearTimeout(temporizador);
  temporizador = setTimeout(() => refrescar(productoPreferido), ESPERA_MS);
}

/**
 * @param {HTMLElement} boton
 * @param {string} mensaje
 */
function avisarError(boton, mensaje) {
  const seccion = boton.closest('.banva-sug');
  if (!seccion) return;
  let aviso = seccion.querySelector('.banva-sug__aviso');
  if (!aviso) {
    aviso = document.createElement('p');
    aviso.className = 'banva-sug__aviso';
    aviso.setAttribute('role', 'alert');
    seccion.append(aviso);
  }
  aviso.textContent = mensaje;
}

document.addEventListener(ThemeEvents.cartUpdate, (evento) => {
  const detalle = /** @type {CustomEvent} */ (evento).detail;
  programar(detalle?.data?.productId);
});

document.addEventListener('click', async (evento) => {
  const boton = /** @type {HTMLElement | null} */ (
    evento.target instanceof Element ? evento.target.closest('[data-banva-sug-agregar]') : null
  );
  if (!boton || boton.hasAttribute('disabled')) return;

  boton.setAttribute('disabled', '');
  boton.setAttribute('aria-busy', 'true');
  const titulo = boton.dataset.titulo ?? 'el producto';
  try {
    const secciones = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((c) => /** @type {HTMLElement} */ (c).dataset.sectionId)
          .filter(Boolean)
      ),
    ];
    const cuerpo = new FormData();
    cuerpo.append('id', boton.dataset.variante ?? '');
    cuerpo.append('quantity', '1');
    if (secciones.length) cuerpo.append('sections', secciones.join(','));

    const r = await fetch(window.Theme?.routes?.cart_add_url ?? `${raizDeRutas()}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      body: cuerpo,
    });
    const json = await r.json();
    if (json.status) {
      // p. ej. «no hay más stock»: se le dice al cliente en el mismo lugar, no se traga.
      console.error('[banva-sug] Shopify rechazó el agregado', json);
      avisarError(boton, json.description || json.message || `No se pudo agregar «${titulo}».`);
      return;
    }
    boton.dispatchEvent(
      new CartAddEvent({}, String(boton.dataset.variante), {
        source: 'banva-sugerencias',
        itemCount: 1,
        productId: boton.dataset.producto,
        sections: json.sections,
      })
    );
    // Ya está en el carro: la tarjeta sale al tiro (el refresco la confirmará en ~1 s) para que nadie la toque dos veces.
    boton.closest('.banva-sug__item')?.remove();
  } catch (error) {
    console.error('[banva-sug] fallo al agregar', error);
    avisarError(boton, `No se pudo agregar «${titulo}». Inténtalo de nuevo.`);
  } finally {
    boton.removeAttribute('disabled');
    boton.removeAttribute('aria-busy');
  }
});

// Carga inicial: si el carro ya tenía productos, el contenedor existe (sólo se dibuja con carro no vacío).
if (document.querySelector(CONTENEDOR)) {
  if ('requestIdleCallback' in window) requestIdleCallback(() => refrescar(undefined));
  else setTimeout(() => refrescar(undefined), 1500);
}
